"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var SimpleAPIError_1 = require("./Errors/SimpleAPIError");
var SimpleAPIValidationError_1 = require("./Errors/SimpleAPIValidationError");
var SimpleAPIData_1 = require("./SimpleAPIData");
var SimpleAPIResponse = /** @class */ (function () {
    function SimpleAPIResponse() {
        this.parsedErrors = null;
        this.parsedData = null;
    }
    /**
     * Checks to see if this object has any data.
     * @returns {boolean}
     */
    SimpleAPIResponse.prototype.hasData = function () {
        return (this.response.hasOwnProperty("data") && this.response.data.length > 0);
    };
    /**
     * Checks to see if this object has a single (one) data.
     * @returns {boolean}
     */
    SimpleAPIResponse.prototype.hasSingleData = function () {
        return (this.response.hasOwnProperty("data") && this.response.data.length === 1);
    };
    /**
     * Checks to see if this object is made up of one or more errors.
     * @returns {Boolean}
     */
    SimpleAPIResponse.prototype.isError = function () {
        return (this.response.hasOwnProperty("errors") ||
            (this.hasSingleData() &&
                this.response.data[0].hasOwnProperty("attributes") &&
                this.response.data[0].attributes.hasOwnProperty("success") &&
                this.response.data[0].attributes.success === false));
    };
    /**
     * Checks to see if the error response is a 500 error
     * @returns {Boolean}
     */
    SimpleAPIResponse.prototype.isServerError = function () {
        return (this.response.hasOwnProperty("errors") &&
            Array.isArray(this.response.errors) &&
            this.response.errors.length === 1 &&
            this.response.errors[0].hasOwnProperty("code") &&
            this.response.errors[0].code === 500);
    };
    /**
     * Checks to see if the response is a single forbidden error.
     * @returns {Boolean}
     */
    SimpleAPIResponse.prototype.isForbiddenError = function () {
        return (this.response.hasOwnProperty("errors") &&
            Array.isArray(this.response.errors) &&
            this.response.errors.length === 1 &&
            this.response.errors[0].hasOwnProperty("code") &&
            this.response.errors[0].code === 403);
    };
    /**
     * If it's a server error message, get the message.
     * @returns {boolean|string}
     */
    SimpleAPIResponse.prototype.getServerErrorMessage = function () {
        if (this.isServerError()) {
            return this.response.errors[0].message;
        }
        return false;
    };
    /**
     * Gets the errors from an API request
     * @param asMessages {Boolean} If the results should be returned as a array of strings
     * @returns {Array<String|SimpleAPIError>}
     */
    SimpleAPIResponse.prototype.getErrors = function (asMessages) {
        if (asMessages === void 0) { asMessages = false; }
        var errors = [];
        /*
         * If we haven't cached the errors, then parse and cache them.
         */
        if (this.parsedErrors === null) {
            /*
             * If this object is an error, then process it as an error.
             */
            if (this.isError()) {
                /*
                 * If there's a collection of errors, loop over all of them.
                 */
                if (Array.isArray(this.response.errors)) {
                    for (var i in this.response.errors) {
                        if (this.response.errors.hasOwnProperty(i)) {
                            var error = this.response.errors[i];
                            if (error.hasOwnProperty("data")) {
                                error = error.data;
                            }
                            /*
                             * If the error has a code of 417, then it's a validation error.
                             */
                            if (String(error.code) === "417" ||
                                String(error.status) === "417") {
                                errors.push(new SimpleAPIValidationError_1.SimpleAPIValidationError(error));
                            }
                            else {
                                errors.push(new SimpleAPIError_1.SimpleAPIError(error));
                            }
                        }
                    }
                    /*
                     * If it's a single error, then process that the same way.
                     */
                }
                else if (typeof this.response.errors === "object" &&
                    this.response.errors.hasOwnProperty("detail")) {
                    errors.push(new SimpleAPIError_1.SimpleAPIError(this.response.errors));
                }
                /*
                 * If it's not an error but has data and success is false,
                 * then treat it as an error.
                 */
                if (this.hasSingleData()) {
                    var data = this.getSingleData();
                    if (data.get("success") === false) {
                        errors.push(new SimpleAPIError_1.SimpleAPIError(this.response.data[0]
                            .attributes));
                    }
                }
            }
            this.parsedErrors = errors;
        }
        /*
         * If the output is a collection of string messages, then get the messages and return them.
         */
        if (asMessages) {
            var output = [];
            for (var i in this.parsedErrors) {
                if (this.parsedErrors.hasOwnProperty(i) &&
                    this.parsedErrors[i] instanceof SimpleAPIError_1.SimpleAPIError) {
                    output.push(this.parsedErrors[i].getMessage());
                }
            }
            return output;
        }
        /*
         * Otherwise return the transformed error objects.
         */
        return this.parsedErrors;
    };
    /**
     * Get a collection of errors for a specific field.
     * @param fieldName {string} the name of the field to check for.
     * @returns {SimpleAPIError[]}
     */
    SimpleAPIResponse.prototype.getFieldErrors = function (fieldName) {
        var fields = [];
        /*
         * Get a collection of errors and then loop over them.
         */
        var errors = this.getErrors();
        for (var i in errors) {
            if (errors.hasOwnProperty(i)) {
                if (errors[i] instanceof SimpleAPIValidationError_1.SimpleAPIValidationError) {
                    var error = errors[i];
                    if (error.getField().toLowerCase() === fieldName.toLowerCase()) {
                        fields.push(error);
                    }
                }
            }
        }
        return fields;
    };
    /**
     * Gets a single string representing errors for a specific field
     * @param fieldName {string} The name of the field.
     * @param delimiter {string} The character(s) to join the error messages
     */
    SimpleAPIResponse.prototype.getFieldError = function (fieldName, delimiter) {
        if (delimiter === void 0) { delimiter = ", "; }
        var errors = this.getFieldErrors(fieldName);
        var messages = errors.map(function (e) {
            return e.getMessage();
        });
        return messages.join(delimiter);
    };
    /**
     * The Single Data API response
     * @returns {SimpleAPIData|null}
     */
    SimpleAPIResponse.prototype.getSingleData = function () {
        if (this.hasSingleData()) {
            return new SimpleAPIData_1.SimpleAPIData(this.response.data[0]);
        }
        else {
            return null;
        }
    };
    /**
     * The Single Data API response
     * @returns {SimpleAPIData[]}
     */
    SimpleAPIResponse.prototype.getData = function () {
        var data = [];
        /*
         * If we don't have data cached, then cache it.
         */
        if (this.parsedData === null) {
            if (this.hasData()) {
                for (var i in this.response.data) {
                    if (this.response.data.hasOwnProperty(i)) {
                        data.push(new SimpleAPIData_1.SimpleAPIData(this.response.data[i]));
                    }
                }
            }
            this.parsedData = data;
        }
        /*
         * Return the parsed data objects.
         */
        return this.parsedData;
    };
    /**
     * Check to see if the response has the provided data field
     * @param field {string}
     * @returns {boolean}
     */
    SimpleAPIResponse.prototype.hasMeta = function (field) {
        return (this.response.hasOwnProperty("meta") &&
            this.response.meta.hasOwnProperty(field));
    };
    /**
     * Gets a specific meta field if it exists. If it doesn't then return the
     * fallback value
     * @param field {string}
     * @param fallback {any}
     */
    SimpleAPIResponse.prototype.getMeta = function (field, fallback) {
        if (fallback === void 0) { fallback = null; }
        if (this.hasMeta(field)) {
            return this.response.meta[field];
        }
        return fallback;
    };
    /**
     * Get the total number of results on the response object if it exists.
     * @returns {number|null}
     */
    SimpleAPIResponse.prototype.getTotalResults = function () {
        if (this.response.hasOwnProperty("totalResults")) {
            return this.response.totalResults;
        }
        else {
            return null;
        }
    };
    /**
     * Gets the collection of capabilities
     */
    SimpleAPIResponse.prototype.getCapabilities = function () {
        if (this.response.hasOwnProperty("capabilities")) {
            return this.response.capabilities;
        }
        else {
            return null;
        }
    };
    /**
     * Check to see if the response has the provided capability field
     * @param field {string}
     * @returns {boolean}
     */
    SimpleAPIResponse.prototype.hasCapability = function (field) {
        return (this.response.hasOwnProperty("capabilities") &&
            this.response.capabilities.hasOwnProperty(field));
    };
    /**
     * Gets a specific capability field if it exists. If it doesn't then return the
     * fallback value
     * @param field {string}
     * @param fallback {boolean}
     */
    SimpleAPIResponse.prototype.getCapability = function (field, fallback) {
        if (fallback === void 0) { fallback = false; }
        if (this.hasCapability(field)) {
            return this.response.capabilities[field];
        }
        return fallback;
    };
    /**
     * Sets the response object
     * @param response {SimpleAPIResponseInterface}
     */
    SimpleAPIResponse.prototype.setResponse = function (response) {
        this.response = response;
    };
    return SimpleAPIResponse;
}());
exports.SimpleAPIResponse = SimpleAPIResponse;
//# sourceMappingURL=data:application/json;base64,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